import { ISelectOption } from '@/components/form/fields/SelectField';
import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const optionSchema = z.object({
  option: z.string().nullable(),
});

const projectCustomFieldSchema = z.object({
  id: z.string(),
  custom_field_id: z.string(),
  project_id: z.string(),
});

export const customOptionSchema = z.object({
  id: z.string().optional(),
  custom_field_id: z.string(),
  option: z.string(),
  is_enable: z.boolean(),
});

export const customFieldSchema = z.object({
  id: z.string().optional(),
  module_type: z.string(),
  custom_field_type: z.string(),
  label_field: z.string(),
  is_mandatory: z.any().optional(),
  is_searchable: z.any(),
  project_ids: z.array(z.string()),
  options: z.array(optionSchema).optional(),
  is_enable: z.boolean().optional(),
  project_custom_field: z.array(projectCustomFieldSchema).optional(),
  custom_field_id: z.string().optional(),
});

export type ICustomFieldSchema = z.infer<typeof customFieldSchema> & {
  custom_options?: z.infer<typeof customOptionSchema>[];
};

type ICustomFieldOptions = {
  module_type: ISelectOption[];
  custom_field_type: ISelectOption[];
};

export const CustomFieldService = {
  useCreate: () =>
    useMutation({
      mutationFn: async (data: ICustomFieldSchema) => {
        const response = await axiosInstance.post(
          'api/crud/custom-field',
          data,
        );
        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(
          `/api/crud/custom-field/${id}`,
        );
        return response.data;
      },
    }),

  useGet: (id?: string) =>
    useQuery<ICustomFieldSchema>({
      queryKey: ['custom-field', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(
          `/api/crud/custom-field/${id}`,
        );
        return response.data?.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ICustomFieldSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/custom-field/${data.id}`,
          data,
        );
        return response.data;
      },
    }),

  useBulkCreate: () =>
    useMutation({
      mutationFn: async (data: ICustomFieldSchema[]) => {
        const response = await axiosInstance.post(
          'api/crud/custom-field/bulk',
          data,
        );
        return response.data;
      },
    }),

  useGetByScope: (scope: string) =>
    useQuery<ICustomFieldSchema[]>({
      queryKey: ['custom-field', 'scope', scope],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/api/crud/custom-field/scope/${scope}`,
        );
        return response.data?.data;
      },
    }),

  useGetSpec: () =>
    useQuery<ICustomFieldSchema[]>({
      queryKey: ['custom-field', 'spec'],
      queryFn: async () => {
        const response = await axiosInstance.get('api/crud/custom-field/spec');
        return response.data?.data;
      },
    }),

  useGetOptions: () =>
    useQuery<ICustomFieldOptions>({
      queryKey: ['custom-field-options'],
      queryFn: async () => {
        const { data: optionsData } = await axiosInstance.get(
          '/api/crud/custom-field/options',
        );

        return {
          module_type: optionsData.data.module_type,
          custom_field_type: optionsData.data.custom_field_type,
        };
      },
    }),
};
